import { useFlags } from "launchdarkly-react-client-sdk";

import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import usePatientHeaderReports from "features/patients/patient-header/use-patient-header-reports.hook";
import PreviewSpeechWordRecognitionSpeechAudiometry from "features/patients/sections/patient-audiology/patient-audiology-report/patient-audiology-report-preview/preview-speech-word-recognition/preview-speech-word-recognition-speech-audiometry.component";
import usePureToneAudiogram from "features/patients/sections/patient-audiology/test-results/pure-tone-audiogram/pure-tone-audiogram.hook";
import { AudiogramAnnotationBar } from "features/patients/sections/patient-audiology/test-results/pure-tone-audiogram/pure-tone-chart";
import PureToneGraphMergedView from "features/patients/sections/patient-audiology/test-results/pure-tone-audiogram/pure-tone-chart/pure-tone-graph-merged-view.component";
import { formatBackendDateOnly } from "utils/common";

import { SHOW_SPEECH_AUDIOMETRY_SCORES_ON_LAST_AUDIO } from "../../../../constants/feature-flags";
import PreviewSpeechWordRecognitionWordRecognition from "../../sections/patient-audiology/patient-audiology-report/patient-audiology-report-preview/preview-speech-word-recognition/preview-speech-word-recognition-word-recognition.component";
import HeaderDetailTooltip from "../components/header-detail-tooltip.component";
import HeaderDetail from "./header-detail.component";

export default function HeaderLastReportDate({
  patientId,
  variant = "header"
}: {
  patientId?: number;
  variant?: "header" | "info-row";
} = {}) {
  const { latestAudiogram, isLatestAudiogramLoading } =
    usePatientHeaderReports(patientId);
  const { earsData, hiddenData, hasAudiogramData } = usePureToneAudiogram();

  const flags = useFlags();
  const showSpeechAudiometryScores =
    flags[SHOW_SPEECH_AUDIOMETRY_SCORES_ON_LAST_AUDIO];

  const latestAudiogramDate = latestAudiogram
    ? latestAudiogram?.appointment?.startDate ||
      latestAudiogram?.reportDate ||
      latestAudiogram?.noah_session?.session_date ||
      latestAudiogram?.created_at
    : null;

  const formattedDate = latestAudiogramDate
    ? formatBackendDateOnly(latestAudiogramDate, "MM/dd/yyyy")
    : "";

  if (isLatestAudiogramLoading) {
    return (
      <Box aria-busy="true" aria-label="Loading last audiogram date">
        <Skeleton variant="text" width={120} />
      </Box>
    );
  }

  if (!latestAudiogram) {
    return variant === "header" ? (
      <Box sx={{ fontSize: "16px" }}>&nbsp;</Box>
    ) : null;
  }

  const speechAudiometryReport = latestAudiogram?.speechAudiometryReport;
  const wordRecognitionData = latestAudiogram?.wordRecognitionReport;
  const additionalWordRecognitionData =
    latestAudiogram?.additionalWordRecognitionReport;

  // Callers key the tooltip on the audiogram id: the header stays mounted
  // when the user moves to another patient, and a preview pinned open for one
  // patient must not stay open (or reopen by itself) on the next.
  const tooltipProps = {
    tooltip: (
      <Box sx={{ maxHeight: "80vh", overflowY: "auto" }}>
        <AudiogramAnnotationBar
          annotation={
            latestAudiogram?.pureToneAudiogramReport?.audiogramAnnotation
          }
        />
        <PureToneGraphMergedView
          earsData={earsData}
          hiddenData={hiddenData}
          displayLegend={false}
          copyButtonTop
        />
        {showSpeechAudiometryScores && (
          <>
            <PreviewSpeechWordRecognitionSpeechAudiometry
              data={speechAudiometryReport}
              detailSize={1}
              wordListSize={3}
              titleSize={12}
              pb={2}
              titleMb={1.5}
              title="Speech Audiometry"
            />

            <PreviewSpeechWordRecognitionWordRecognition
              title="Word Recognition"
              data={wordRecognitionData}
              additionalData={additionalWordRecognitionData}
              detailSize={1}
              wordListSize={3}
              titleSize={12}
              pb={2}
              titleMb={1.5}
            />
          </>
        )}
      </Box>
    ),
    disableTooltip: !hasAudiogramData,
    sx: { minWidth: "950px" },
    pinnable: { title: `Last Audiogram · ${formattedDate}` }
  };

  if (variant === "info-row") {
    return (
      <HeaderDetailTooltip key={latestAudiogram.id} {...tooltipProps}>
        <Stack direction="row" gap={1.5}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 72, flexShrink: 0 }}
          >
            Last Audiogram
          </Typography>
          <Typography
            variant="body2"
            sx={{ cursor: hasAudiogramData ? "pointer" : undefined }}
          >
            {formattedDate}
          </Typography>
        </Stack>
      </HeaderDetailTooltip>
    );
  }

  return (
    <HeaderDetailTooltip key={latestAudiogram.id} {...tooltipProps}>
      <HeaderDetail
        emptyValueText=""
        value={`Last Audiogram: ${formattedDate}`}
        sx={{ cursor: hasAudiogramData ? "pointer" : undefined }}
      />
    </HeaderDetailTooltip>
  );
}
